import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PLAN_KEYS = ["professional", "enterprise"] as const;

const PLAN_META = {
  professional: { ctaHref: "/login" as const, highlighted: true, metered: true },
  enterprise: { ctaHref: null, highlighted: false, metered: false },
} as const;

export default function PublicPlanCards() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2">
      {PLAN_KEYS.map((key) => {
        const meta = PLAN_META[key];
        const rawFeatures = t(`public.pricing.plans.${key}.features`, {
          returnObjects: true,
        }) as string[];
        const features = Array.isArray(rawFeatures) ? rawFeatures : [];
        return (
          <div
            key={key}
            className={`flex flex-col rounded-xl border p-6 ${
              meta.highlighted
                ? "border-primary ring-2 ring-primary"
                : "border-border"
            }`}
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="font-semibold">
                {t(`public.pricing.plans.${key}.name`)}
              </span>
              {meta.highlighted ? (
                <Badge variant="secondary">{t("public.pricing.mostPopular")}</Badge>
              ) : null}
            </div>
            <div className="mb-2 flex items-baseline gap-1">
              <span className="text-3xl font-bold">
                {t(`public.pricing.plans.${key}.price`)}
              </span>
              {meta.metered ? (
                <span className="text-sm text-muted-foreground">
                  {t("public.pricing.perMonth")}
                </span>
              ) : null}
            </div>
            <p className="mb-6 text-sm text-muted-foreground">
              {t(`public.pricing.plans.${key}.description`)}
            </p>
            <ul className="mb-8 flex-1 space-y-2">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  {feature}
                </li>
              ))}
            </ul>
            {meta.ctaHref ? (
              <Link
                to={meta.ctaHref}
                className={buttonVariants({
                  variant: meta.highlighted ? "default" : "outline",
                  className: "w-full",
                })}
              >
                {t(`public.pricing.plans.${key}.cta`)}
              </Link>
            ) : (
              <span
                className={buttonVariants({
                  variant: "outline",
                  className: "w-full",
                })}
              >
                {t(`public.pricing.plans.${key}.cta`)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
